import { Card, CardBody, CardHeader, CardFooter, Switch, Slider, Modal, ModalBody, ModalContent, ModalHeader, Button, Select, SelectItem, ModalFooter, Avatar, Divider, Chip, Input } from "@nextui-org/react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { CtyButton } from "../button/CtyButton";
import { azureSupportedLanguages } from "../utils/constants";
import {
  ISO_COUNTRIES,
  ISO_LANGUAGES,
  getIsoCountryByCode,
  getIsoLanguageByCode,
  normalizeAdvisorRegions,
  mergeAdvisorRegions,
  type AdvisorRegionSetting,
  type AdvisorRegionLanguage,
} from "../utils/isoRegionConfig";
import {
  filterIsoCountriesByRegions,
  filterIsoLanguagesByRegion,
} from "../utils/conversationRegionUtils";
import { useTranslation } from "react-i18next";
import { CtySpinner } from "../spinner/CtySpinner";

export enum SETTING_TYPE {
    DEPTH = 'depth',
    COT = 'CoT',
    AUTO_TRAIN = 'auto_train',
    SPEAK = 'voice',
    TRANSLATION = 'translation',
    ZAPIER = 'zapier',
    EXPLAIN_ANSWER = 'explain_answer',
    IMAGES = 'images',
    SCHEMATIC_DIAGRAMS = 'schematic diagrams',
    VIDEO = 'video',
    AUDIO = 'audio',
    TEMPRATURE = 'temperature',
    COUNTRY = 'country',
    RUM = 'rum',
}

export enum SETTING_SCOPE {
    APP = 'app',
    ADVISOR = 'advisor',
    VOICE = 'voice',
    TRANSLATION = 'translation',
}

const normalizeAbilityKey = (ability?: string) => String(ability ?? '').toLowerCase();

const isBehaviorAbility = (ability?: string) => {
    const key = normalizeAbilityKey(ability);
    return key === SETTING_TYPE.DEPTH || key === SETTING_TYPE.TEMPRATURE || key === SETTING_TYPE.RUM;
};

const isRumAbility = (ability?: string) => normalizeAbilityKey(ability) === SETTING_TYPE.RUM;

/** Max country rows shown before the list scrolls (~5 rows including gap). */
const REGION_PREVIEW_MAX_HEIGHT = '11rem';

export type SettingsIntegrationSectionType = 'behavior' | 'capabilities';

export type VoiceAgentOption = { id: string; name: string };

interface SettingsIntegrationCardProps {
    integrationsData: any;
    updateIntegration: (item: any, index: number, updateActive?: boolean) => void;
    integrationScope: SETTING_SCOPE;
    handleUpdateLanguageOptions: (item: any, selectedKeys: any) => void;
    /** Advisor voice agent picker: updates metadata.selected_voice_agent (same pattern as translation). */
    handleUpdateVoiceAgent?: (item: any, selectedVoiceAgentId: string) => void | Promise<void>;
    /** Advisor country regions: updates metadata.regions (same pattern as translation / voice). */
    handleUpdateCountryRegions?: (item: any, regions: AdvisorRegionSetting[]) => void | Promise<void>;
    /** Tenant-configured regions used to limit advisor region picker options. */
    tenantRegionOptions?: AdvisorRegionSetting[];
    /** Tenant-configured translation languages used to limit advisor language picker options. */
    tenantTranslationLanguages?: string[];
    /** Whether Country is enabled at tenant level (used to disable advisor Translation). */
    tenantCountryEnabled?: boolean;
    voiceAgents?: VoiceAgentOption[];
    isVoiceAgentsLoading?: boolean;
    isUpdateSettingsLoading?: boolean;
    /** When set, shows section-specific UI: behavior = sliders + View Integration icon; capabilities = switches + View Integration icon */
    currentSection?: SettingsIntegrationSectionType;
    isDisabled?: boolean;
}

const SettingsIntegrationCard = (props: SettingsIntegrationCardProps) => {
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [currentActiveItem, setCurrentActiveItem] = useState<any>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { integrationsData, updateIntegration, integrationScope, handleUpdateLanguageOptions, handleUpdateVoiceAgent, handleUpdateCountryRegions, tenantRegionOptions = [], tenantTranslationLanguages = [], tenantCountryEnabled = false, voiceAgents = [], isVoiceAgentsLoading = false, currentSection, isUpdateSettingsLoading = false, isDisabled = false } = props;
    const [activeModal, setActiveModal] = useState<'languages' | 'voice' | 'region' | null>(null);
    const [selectedVoiceAgentKey, setSelectedVoiceAgentKey] = useState<string>('');
    const [editingRegionCountryCode, setEditingRegionCountryCode] = useState<string>('');
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
    const [selectedLanguageCodes, setSelectedLanguageCodes] = useState<string[]>([]);
    const [countrySearch, setCountrySearch] = useState('');
    const [languageSearch, setLanguageSearch] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    const displayData = useMemo(() => {
        const list = integrationsData || [];
        if (currentSection === 'behavior') {
            return list.filter((item: any) => isBehaviorAbility(item.ability));
        }
        if (currentSection === 'capabilities') {
            return list.filter((item: any) => !isBehaviorAbility(item.ability));
        }
        return list;
    }, [integrationsData, currentSection]);

    const handleCloseModal = () => {
        if (!isUpdating) {
            setActiveModal(null);
            setSelectedKeys([]);
            setSelectedVoiceAgentKey('');
            setSelectedCountryCode('');
            setSelectedLanguageCodes([]);
            setCountrySearch('');
            setLanguageSearch('');
            setEditingRegionCountryCode('');
            setCurrentActiveItem(null);
        }
    };

    function viewIntegration(type: SETTING_TYPE): string {
        if (integrationScope === SETTING_SCOPE.APP) {
            return type === SETTING_TYPE.ZAPIER ? "/settings/zapier" : '';
        }
        return '';

    }

    const formatText = (input: string) => input.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    const renderSlider = (settingItem: any, index: number) => {
        const abilityKey = normalizeAbilityKey(settingItem.ability);
        const isRum = abilityKey === SETTING_TYPE.RUM;
        const showSlider =
            abilityKey === SETTING_TYPE.DEPTH ||
            abilityKey === SETTING_TYPE.TEMPRATURE ||
            isRum;
        if (!showSlider) return null;

        let defaultValue = 2;
        let start = 1;
        let end = 5;
        let step = 1;
        let marks: { value: number; label: string }[] = [
            { value: 1, label: '1' },
            { value: 2, label: '2' },
            { value: 3, label: '3' },
            { value: 4, label: '4' },
            { value: 5, label: '5' },
        ];

        if (abilityKey === SETTING_TYPE.TEMPRATURE) {
            defaultValue = 0.5;
            start = 0;
            end = 1;
            step = 0.25;
            marks = [
                { value: 0, label: '0' },
                { value: 0.25, label: '0.25' },
                { value: 0.5, label: '0.5' },
                { value: 0.75, label: '0.75' },
                { value: 1, label: '1' },
            ];
        } else if (isRum) {
            defaultValue = Number(settingItem.default_value ?? 0);
            start = 0;
            end = 100;
            step = 1;
            marks = [
                { value: 0, label: '0' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 75, label: '75' },
                { value: 100, label: '100' },
            ];
        }

        const sliderValue = Number(
            settingItem.ability_value ?? settingItem.value ?? settingItem.default_value ?? defaultValue,
        );

        function updateValue(value: number) {
            const next = value ?? defaultValue;
            const payLoad = { ...settingItem, value: next, ability_value: next };
            if (settingItem.is_active) {
                updateIntegration(payLoad, index, true);
            }
        }
        return (
            <Slider
                aria-labelledby="slider"
                size="sm"
                step={step}
                color="primary"
                showSteps={true}
                isDisabled={isDisabled || !settingItem.is_active}
                maxValue={end}
                minValue={start}
                value={sliderValue}
                marks={marks}
                onChangeEnd={(value) => updateValue(value as number)}
            />
        );
    }
    function renderViewIntegrationLink(item: any, integrationScope: SETTING_SCOPE, navigate: ReturnType<typeof useNavigate>, asIconOnly?: boolean) {
        const isVoiceAbility = item.ability === SETTING_TYPE.SPEAK;
        const isCountryAbility = item.ability === SETTING_TYPE.COUNTRY;
        const isTranslationAbility = item.ability === SETTING_TYPE.TRANSLATION;
        const isAdvisorTranslationPickerDisabled =
            integrationScope === SETTING_SCOPE.ADVISOR &&
            isTranslationAbility &&
            tenantTranslationLanguages.length === 0;
        const isTranslationControlDisabled =
            isTranslationAbility && (isCountryFeatureActive || isAdvisorTranslationPickerDisabled);
        const showViewLink =
            item.ability === SETTING_TYPE.TRANSLATION ||
            (isCountryAbility && Boolean(handleUpdateCountryRegions)) ||
            (integrationScope === SETTING_SCOPE.APP && !isVoiceAbility && !isCountryAbility) ||
            (isVoiceAbility && (integrationScope === SETTING_SCOPE.ADVISOR || Boolean(handleUpdateVoiceAgent)));
        if (!showViewLink) return null;
        const isAppIntegration = integrationScope === SETTING_SCOPE.APP;
        const linkHref = isAppIntegration && viewIntegration(item.ability);
        const handleViewIntegration = () => {
            if (item.ability === SETTING_TYPE.COUNTRY && handleUpdateCountryRegions) {
                openRegionModal(item);
                return;
            }
            if (isAppIntegration && typeof linkHref === 'string' && linkHref) {
                navigate(linkHref);
            } else if (isVoiceAbility && (integrationScope === SETTING_SCOPE.ADVISOR || handleUpdateVoiceAgent)) {
                setActiveModal('voice');
                setCurrentActiveItem(item);
                setSelectedVoiceAgentKey(String(item.metadata?.selected_voice_agent ?? ''));
            } else {
                setActiveModal('languages');
                setCurrentActiveItem(item);
                const currentLanguages: string[] = item.metadata?.selected_languages ?? ['English'];
                const initialLanguages =
                    integrationScope === SETTING_SCOPE.ADVISOR && tenantTranslationLanguages.length > 0
                        ? currentLanguages.filter((lang) => tenantTranslationLanguages.includes(lang))
                        : currentLanguages;
                setSelectedKeys(initialLanguages.length > 0 ? initialLanguages : ['English']);
            }
        };
        const linkLabel = isVoiceAbility ? t('View voice agent') : t('View integration');
        const isLinkDisabled = !item.is_active || isTranslationControlDisabled;
        if (asIconOnly) {
            return (
                <CtyButton onPress={handleViewIntegration} className="text-sm font-semibold text-primary px-0 min-w-10" variant="light" isDisabled={isLinkDisabled}>
                    <Icon icon="mdi:open-in-new" width={18} height={18} />
                </CtyButton>
            );
        }
        return (
            <CtyButton onPress={handleViewIntegration} className="text-sm font-semibold text-primary" variant="light" isDisabled={isLinkDisabled}>
                {linkLabel}
            </CtyButton>
        );
    }
    const handleLanguageSelectionChange = (keys: any) => {
        const keysArray: string[] = Array.from(keys);
        setSelectedKeys(keysArray);
    };

    const handleVoiceAgentSelectionChange = (keys: any) => {
        const keysArray: string[] = Array.from(keys as Set<string>);
        setSelectedVoiceAgentKey(keysArray[0] ?? '');
    };

    const onPressUpdateVoiceAgent = async () => {
        const metaAgent = currentActiveItem?.metadata?.selected_voice_agent;
        const voiceId =
            selectedVoiceAgentKey ||
            (metaAgent != null && String(metaAgent) !== '' ? String(metaAgent) : '');
        if (!currentActiveItem || !handleUpdateVoiceAgent || !voiceId) return;
        setIsUpdating(true);
        try {
            await Promise.resolve(handleUpdateVoiceAgent(currentActiveItem, voiceId));
            setActiveModal(null);
            setSelectedVoiceAgentKey('');
            setCurrentActiveItem(null);
        } catch {
            /* Parent handler shows toast */
        } finally {
            setIsUpdating(false);
        }
    };

    const openRegionModal = (item: any, countryCode = '') => {
        setCurrentActiveItem(item);
        setEditingRegionCountryCode(countryCode.toUpperCase());
        if (countryCode) {
            const regions = normalizeAdvisorRegions(item.metadata?.regions);
            const region = regions.find((entry) => entry.country_code === countryCode.toUpperCase());
            setSelectedCountryCode(region?.country_code ?? '');
            setSelectedLanguageCodes(region?.languages.map((language) => language.code) ?? []);
        } else {
            setSelectedCountryCode('');
            setSelectedLanguageCodes([]);
        }
        setCountrySearch('');
        setLanguageSearch('');
        setActiveModal('region');
    };

    const handleCountrySelectionChange = (keys: any) => {
        const keysArray: string[] = Array.from(keys as Set<string>);
        const countryCode = keysArray[0] ?? '';
        setSelectedCountryCode(countryCode);
        setCountrySearch('');

        if (!countryCode) {
            setSelectedLanguageCodes([]);
            return;
        }

        const existingRegions = currentActiveItem
            ? normalizeAdvisorRegions(currentActiveItem.metadata?.regions)
            : [];
        const existing = existingRegions.find(
            (entry) => entry.country_code === countryCode.toUpperCase()
        );
        const isEditingSameCountry =
            editingRegionCountryCode &&
            editingRegionCountryCode.toUpperCase() === countryCode.toUpperCase();

        if (existing && !isEditingSameCountry) {
            setSelectedLanguageCodes(existing.languages.map((language) => language.code));
            return;
        }

        if (!editingRegionCountryCode) {
            setSelectedLanguageCodes([]);
        }
    };

    const handleRegionLanguageSelectionChange = (keys: any) => {
        const keysArray: string[] = Array.from(keys as Set<string>);
        setSelectedLanguageCodes(keysArray);
    };

    const availableCountries = useMemo(() => {
        if (integrationScope === SETTING_SCOPE.ADVISOR && tenantRegionOptions.length > 0) {
            return filterIsoCountriesByRegions(tenantRegionOptions);
        }
        return ISO_COUNTRIES;
    }, [integrationScope, tenantRegionOptions]);

    const availableLanguagesForCountry = useMemo(() => {
        if (integrationScope === SETTING_SCOPE.ADVISOR && tenantRegionOptions.length > 0) {
            if (!selectedCountryCode) return [];
            return filterIsoLanguagesByRegion(tenantRegionOptions, selectedCountryCode);
        }
        return ISO_LANGUAGES;
    }, [integrationScope, tenantRegionOptions, selectedCountryCode]);

    const availableTranslationLanguages = useMemo(() => {
        const allLanguages = Object.values(azureSupportedLanguages);
        if (integrationScope === SETTING_SCOPE.ADVISOR && tenantTranslationLanguages.length > 0) {
            const tenantLanguageSet = new Set(tenantTranslationLanguages);
            return allLanguages.filter((language) => tenantLanguageSet.has(language));
        }
        return allLanguages;
    }, [integrationScope, tenantTranslationLanguages]);

    const filteredCountries = useMemo(() => {
        const query = countrySearch.trim().toLowerCase();
        if (!query) return availableCountries;
        return availableCountries.filter(
            (country) =>
                country.name.toLowerCase().includes(query) ||
                country.code.toLowerCase().includes(query)
        );
    }, [countrySearch, availableCountries]);

    const filteredLanguages = useMemo(() => {
        const query = languageSearch.trim().toLowerCase();
        if (!query) return availableLanguagesForCountry;
        return availableLanguagesForCountry.filter(
            (language) =>
                language.name.toLowerCase().includes(query) ||
                language.code.toLowerCase().includes(query)
        );
    }, [languageSearch, availableLanguagesForCountry]);

    const regionSelectClassNames = {
        trigger: 'bg-content1 border-default-200 shadow-none ring-0 outline-none data-[hover=true]:border-default-300 data-[open=true]:border-default-300 data-[focus=true]:border-default-300',
        popoverContent: 'border border-default-200 p-0 shadow-md',
        listbox: 'max-h-60 overflow-y-auto',
        listboxWrapper: 'border-0 shadow-none',
    };

    const regionSearchInputClassNames = {
        inputWrapper: 'border-default-200 bg-content1 shadow-none ring-0 outline-none group-data-[focus=true]:border-default-300',
        input: 'outline-none',
    };

    const renderSelectSearchInput = (
        value: string,
        onValueChange: (next: string) => void,
        placeholder: string,
        inputId: string
    ) => (
        <div className="px-2 py-2 border-b border-default-200 sticky top-0 z-10 bg-content1">
            <Input
                id={inputId}
                size="sm"
                variant="bordered"
                placeholder={placeholder}
                value={value}
                onValueChange={onValueChange}
                isClearable
                onClear={() => onValueChange('')}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                classNames={regionSearchInputClassNames}
            />
        </div>
    );

    const onPressSaveRegion = async () => {
        if (!currentActiveItem || !handleUpdateCountryRegions) return;
        if (!selectedCountryCode || !selectedLanguageCodes.length) return;

        if (
            integrationScope === SETTING_SCOPE.ADVISOR &&
            tenantRegionOptions.length > 0
        ) {
            const tenantRegion = tenantRegionOptions.find(
                (region) => region.country_code === selectedCountryCode.toUpperCase()
            );
            if (!tenantRegion) {
                return;
            }
            const allowedLanguageCodes = new Set(
                tenantRegion.languages.map((language) => language.code.toLowerCase())
            );
            const invalidLanguage = selectedLanguageCodes.some(
                (code) => !allowedLanguageCodes.has(code.toLowerCase())
            );
            if (invalidLanguage) return;
        }

        const country = getIsoCountryByCode(selectedCountryCode);
        const languages = selectedLanguageCodes.map((code) => {
            const iso = getIsoLanguageByCode(code);
            return {
                code: code.toLowerCase(),
                name: iso?.name ?? code,
            };
        });
        const nextRegion: AdvisorRegionSetting = {
            country_code: selectedCountryCode.toUpperCase(),
            country_name: country?.name ?? selectedCountryCode,
            languages,
        };

        const existingRegions = normalizeAdvisorRegions(currentActiveItem.metadata?.regions);
        const countryCodesToReplace = new Set<string>([
            nextRegion.country_code,
            ...(editingRegionCountryCode ? [editingRegionCountryCode.toUpperCase()] : []),
        ]);
        const updatedRegions = mergeAdvisorRegions([
            ...existingRegions.filter((region) => !countryCodesToReplace.has(region.country_code)),
            nextRegion,
        ]);

        setIsUpdating(true);
        try {
            await Promise.resolve(handleUpdateCountryRegions(currentActiveItem, updatedRegions));
            handleCloseModal();
        } catch {
            /* Parent handler shows toast */
        } finally {
            setIsUpdating(false);
        }
    };

    const onPressRemoveRegion = async (item: any, countryCode: string) => {
        if (!handleUpdateCountryRegions) return;
        const existingRegions = normalizeAdvisorRegions(item.metadata?.regions);
        const updatedRegions = existingRegions.filter(
            (region) => region.country_code !== countryCode.toUpperCase()
        );
        setIsUpdating(true);
        try {
            await Promise.resolve(handleUpdateCountryRegions(item, updatedRegions));
        } catch {
            /* Parent handler shows toast */
        } finally {
            setIsUpdating(false);
        }
    };

    const fullData = integrationsData || [];
    const getGlobalIndex = (item: any) => fullData.findIndex((d: any) => d.ability === item.ability);

    const isCountryEnabled = useMemo(() => {
        const countryItem = fullData.find((entry: any) => entry.ability === SETTING_TYPE.COUNTRY);
        return Boolean(countryItem?.is_active);
    }, [fullData]);

    const isCountryFeatureActive = useMemo(
        () => isCountryEnabled || (integrationScope === SETTING_SCOPE.ADVISOR && tenantCountryEnabled),
        [integrationScope, isCountryEnabled, tenantCountryEnabled]
    );

    const isTranslationDisabledByCountry = (item: any) =>
        item.ability === SETTING_TYPE.TRANSLATION && isCountryFeatureActive;

    const getIntegrationSwitchSelected = (item: any) =>
        isTranslationDisabledByCountry(item) ? false : item.is_active;

    const isIntegrationSwitchDisabled = (item: any) =>
        isDisabled || isTranslationDisabledByCountry(item);

    const handleIntegrationToggle = (item: any, index: number, updateActive?: boolean) => {
        const isActive = updateActive ?? !item.is_active;
        if (item.ability === SETTING_TYPE.COUNTRY && isActive) {
            const translationItem = fullData.find((entry: any) => entry.ability === SETTING_TYPE.TRANSLATION);
            if (translationItem?.is_active) {
                const translationIndex = getGlobalIndex(translationItem);
                if (translationIndex !== -1) {
                    updateIntegration(translationItem, translationIndex, false);
                }
            }
        }
        updateIntegration(item, index, isActive);
    };

    const renderBehavior = (item: any, index: number) => {
        return (<Card className="border border-default-200 shadow-sm" key={item.ability} shadow="none">
            <CardHeader className="flex justify-between items-start gap-4 pb-2">
                <div>
                    <p className="text-base font-semibold text-foreground">{formatText(item.ability)}</p>
                    <p className="text-sm text-default-500 mt-1">{item.description}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Switch
                        isSelected={getIntegrationSwitchSelected(item)}
                        size="sm"
                        color="primary"
                        isDisabled={isIntegrationSwitchDisabled(item)}
                        onChange={() => handleIntegrationToggle(item, index, !item.is_active)}
                        aria-label={t('Enable')}
                    />
                    {renderViewIntegrationLink(item, integrationScope, navigate, true)}
                </div>
            </CardHeader>
            <CardBody className="pt-0">
                <div className="w-full">{renderSlider(item, index)}</div>
            </CardBody>
        </Card>)
    }

    const renderCapability = (item: any, index: number) => {
        const isCountry = item.ability === SETTING_TYPE.COUNTRY;
        return (
            <div
                key={item.ability}
                className={`flex items-center gap-4 p-3 ${index < displayData.length - 1 ? 'border-b border-default-200' : ''} hover:bg-default-50/50 transition-colors`}
            >
                <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold text-foreground">{formatText(item.ability)}</p>
                    {item.description && (
                        <p className="text-sm text-default-500 mt-0.5">{item.description}</p>
                    )}
                    {isCountry && renderRegionPreview(item)}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                    <Switch
                        isSelected={getIntegrationSwitchSelected(item)}
                        size="sm"
                        color="primary"
                        isDisabled={isIntegrationSwitchDisabled(item)}
                        onChange={() => handleIntegrationToggle(item, index, !item.is_active)}
                        aria-label={t('Enable')}
                    />
                    {renderViewIntegrationLink(item, integrationScope, navigate, true)}
                </div>
            </div>
        )
    }

    const renderRegionPreview = (item: any) => {
        const regions = normalizeAdvisorRegions(item.metadata?.regions);
        if (!regions.length) return null;

        return (
            <div
                className="flex flex-col gap-2 mt-2 overflow-y-auto pr-1 w-full"
                style={{ maxHeight: REGION_PREVIEW_MAX_HEIGHT }}
                aria-label={t('Country regions')}
            >
                {regions.map((region) => (
                    <div key={region.country_code} className="flex items-center gap-2 min-w-0">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                            <Chip size="sm" variant="flat" className="bg-[#EEF2FF] text-[#4361EE] flex-shrink-0">
                                {region.country_name}
                            </Chip>
                            {region.languages.slice(0, 3).map((language) => (
                                <Chip key={`${region.country_code}-${language.code}`} size="sm" variant="flat" className="bg-[#ECFDF5] text-[#059669] flex-shrink-0 max-w-[88px]">
                                    <span className="truncate">{language.name}</span>
                                </Chip>
                            ))}
                            {region.languages.length > 3 && (
                                <Chip size="sm" variant="flat" className="bg-default-100 text-default-600 flex-shrink-0">
                                    +{region.languages.length - 3}
                                </Chip>
                            )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <CtyButton
                                isIconOnly
                                size="sm"
                                variant="light"
                                className="min-w-7 w-7 h-7"
                                isDisabled={isDisabled || !item.is_active}
                                onPress={() => openRegionModal(item, region.country_code)}
                                title={t('Edit')}
                            >
                                <Icon icon="lucide:pencil" width={14} height={14} />
                            </CtyButton>
                            <CtyButton
                                isIconOnly
                                size="sm"
                                variant="light"
                                className="min-w-7 w-7 h-7 text-danger"
                                isDisabled={isDisabled || !item.is_active}
                                onPress={() => onPressRemoveRegion(item, region.country_code)}
                                title={t('Remove')}
                            >
                                <Icon icon="lucide:x" width={14} height={14} />
                            </CtyButton>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderCountryCard = (item: any, index: number) => (
        <Card className="border cursor-pointer min-h-[210px]" key={item.ability + index} shadow="sm">
            <CardHeader className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <Avatar
                        size="md"
                        showFallback
                        name={item.ability?.[0]?.toUpperCase() ?? ''}
                        alt="Profile"
                        classNames={{ base: 'bg-[#f0f1f2] text-default-600' }}
                    />
                    <p className="text-md font-semibold">{formatText(item.ability)}</p>
                </div>
                <Switch
                    isSelected={item.is_active}
                    size="sm"
                    isDisabled={isDisabled}
                    onChange={() => handleIntegrationToggle(item, index, !item.is_active)}
                    aria-label={t('Automatic updates')}
                />
            </CardHeader>
            <CardBody className="overflow-hidden flex flex-col justify-between">
                <div className="text-sm font-normal truncate-2-lines">
                    {item.description}
                </div>
                {renderRegionPreview(item)}
            </CardBody>
            <Divider />
            <CardFooter className="flex justify-end">
                <CtyButton
                    onPress={() => openRegionModal(item)}
                    className="text-sm font-semibold text-primary border border-default-200 rounded-lg px-3"
                    variant="light"
                    isDisabled={
                        !item.is_active ||
                        isDisabled ||
                        !handleUpdateCountryRegions ||
                        (integrationScope === SETTING_SCOPE.ADVISOR && tenantRegionOptions.length === 0)
                    }
                    startContent={<Icon icon="lucide:plus" width={14} height={14} />}
                >
                    {t('Add Region')}
                </CtyButton>
            </CardFooter>
        </Card>
    );

    const renderAllItemsCard = (item: any, index: number) => {
        if (item.ability === SETTING_TYPE.COUNTRY) {
            return renderCountryCard(item, index);
        }
        return (
        <Card className="border cursor-pointer h-[210px]" key={item.ability + index} shadow="sm">
            <CardHeader className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <Avatar
                        size="md"
                        showFallback
                        name={item.ability?.[0]?.toUpperCase() ?? ''}
                        alt="Profile"
                        classNames={{ base: 'bg-[#f0f1f2] text-default-600' }}
                    />
                    <p className="text-md font-semibold">{formatText(item.ability)}</p>
                </div>
                <Switch
                    isSelected={getIntegrationSwitchSelected(item)}
                    size="sm"
                    isDisabled={isIntegrationSwitchDisabled(item)}
                    onChange={() => handleIntegrationToggle(item, index, !item.is_active)}
                    aria-label={t('Automatic updates')}
                />
            </CardHeader>
            <CardBody className="overflow-hidden flex flex-col justify-between">
                <div className="text-sm font-normal truncate-2-lines">
                    {item.description}
                </div>
                <div className="w-full">{renderSlider(item, index)}</div>
            </CardBody>
            <Divider />
            <CardFooter className="flex justify-end">
                {renderViewIntegrationLink(item, integrationScope, navigate)}
            </CardFooter>
        </Card>
        );
    };

    const getContainerClassName = () => {
        if (currentSection === undefined) {
            return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-3 lg:gap-3 shadow-none';
        }
        if (currentSection === 'capabilities') {
            return 'max-w-2xl rounded-xl border border-default-200 bg-content1 shadow-sm overflow-hidden';
        }
        return 'flex flex-col gap-6 max-w-2xl';
    };
    const className = getContainerClassName();

    const metaVoiceAgent = currentActiveItem?.metadata?.selected_voice_agent;
    const displayedVoiceAgentId =
        selectedVoiceAgentKey ||
        (metaVoiceAgent != null && String(metaVoiceAgent) !== '' ? String(metaVoiceAgent) : '');
    const voiceSelectSelectedKeys = displayedVoiceAgentId ? [displayedVoiceAgentId] : [];

    return (
        <>
        {isUpdateSettingsLoading&&<div className="fixed top-0 left-0 right-0 bottom-0 flex justify-center items-center bg-background/50 z-50">
            <CtySpinner size="lg" color="primary" />
            </div>}
            <div className={className} data-testid="settings-integration-card">
                {displayData.map((item: any) => {
                    const index = getGlobalIndex(item);
                    if (currentSection === 'behavior') {
                        return renderBehavior(item, index);
                    }
                    if (currentSection === 'capabilities') {
                        return renderCapability(item, index);
                    }
                    return renderAllItemsCard(item, index);
                })}
            </div>
            <Modal isOpen={activeModal === 'languages'} placement="top-center" onClose={handleCloseModal} isDismissable={!isUpdating} hideCloseButton={isUpdating}>
                <ModalContent>
                    <>
                        <ModalHeader className="flex flex-col gap-1">{t('Select Language')}</ModalHeader>
                        <ModalBody>
                            <Select
                                label={t('Select options')}
                                selectionMode="multiple"
                                placeholder={t('Select multiple options')}
                                selectedKeys={selectedKeys.length > 0 ? selectedKeys : (currentActiveItem?.metadata?.selected_languages ?? ['English'])}
                                onSelectionChange={handleLanguageSelectionChange}
                            >
                                {availableTranslationLanguages.map((lang: string) => (
                                    <SelectItem key={lang}>{lang}</SelectItem>
                                ))}
                            </Select>
                        </ModalBody>
                        <ModalFooter>
                            <Button onPress={() => handleUpdateLanguageOptions(currentActiveItem, selectedKeys)} color="primary">{t('Update')}</Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>
            <Modal isOpen={activeModal === 'voice'} placement="top-center" onClose={handleCloseModal} isDismissable={!isUpdating} hideCloseButton={isUpdating}>
                <ModalContent>
                    <>
                        <ModalHeader className="flex flex-col gap-1">{t('Select voice agent')}</ModalHeader>
                        <ModalBody>
                            {voiceAgents?.length > 0 ?
                            <>
                            <Select
                                label={t('Voice agent')}
                                selectionMode="single"
                                placeholder={t('Select an option')}
                                isLoading={isVoiceAgentsLoading}
                                selectedKeys={voiceSelectSelectedKeys}
                                onSelectionChange={handleVoiceAgentSelectionChange}
                            >
                                {(voiceAgents ?? []).map((agent) => (
                                    <SelectItem key={agent.id} textValue={agent.name}>
                                        {agent.name}
                                    </SelectItem>
                                ))}
                            </Select>
                            <p className="text-sm text-default-500">Please update the system prompt to include the selected voice agent to configure voice agent properly</p>
                            </>
                             : <p className="text-sm text-default-500">This advisor is not linked to any voice agent</p>}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                onPress={onPressUpdateVoiceAgent}
                                color="primary"
                                isDisabled={!displayedVoiceAgentId}
                            >
                                {t('Update')}
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>
            <Modal isOpen={activeModal === 'region'} placement="center" onClose={handleCloseModal} isDismissable={!isUpdating} hideCloseButton={isUpdating}>
                <ModalContent>
                    <>
                        <ModalHeader className="flex flex-col gap-1 pb-0">
                            <div className="flex items-center gap-3">
                                <Avatar
                                    size="sm"
                                    showFallback
                                    name="C"
                                    classNames={{ base: 'bg-[#EEF2FF] text-[#4361EE]' }}
                                />
                                <span>{editingRegionCountryCode ? t('Edit Region') : t('Add Region')}</span>
                            </div>
                            <p className="text-sm font-normal text-default-500">
                                {integrationScope === SETTING_SCOPE.APP
                                    ? t('Configure countries and languages available for advisors in your tenant.')
                                    : t('Choose countries and languages from your tenant configuration for this advisor.')}
                            </p>
                        </ModalHeader>
                        <ModalBody className="gap-4">
                            <Select
                                label={t('Country')}
                                labelPlacement="outside"
                                placeholder={t('Search or select country')}
                                variant="bordered"
                                selectionMode="single"
                                selectedKeys={selectedCountryCode ? [selectedCountryCode] : []}
                                onSelectionChange={handleCountrySelectionChange}
                                onOpenChange={(open) => { if (!open) setCountrySearch(''); }}
                                startContent={<Icon icon="mdi:earth" width={18} height={18} className="text-default-500" />}
                                listboxProps={{
                                    topContent: renderSelectSearchInput(
                                        countrySearch,
                                        setCountrySearch,
                                        t('Search countries'),
                                        'region-country-search'
                                    ),
                                }}
                                popoverProps={{ placement: 'bottom' }}
                                classNames={regionSelectClassNames}
                            >
                                {filteredCountries.map((country) => (
                                    <SelectItem key={country.code} textValue={country.name}>
                                        {country.name}
                                    </SelectItem>
                                ))}
                            </Select>
                            <Select
                                label={t('Language')}
                                labelPlacement="outside"
                                placeholder={t('Search or select languages')}
                                variant="bordered"
                                selectionMode="multiple"
                                selectedKeys={selectedLanguageCodes}
                                onSelectionChange={handleRegionLanguageSelectionChange}
                                onOpenChange={(open) => { if (!open) setLanguageSearch(''); }}
                                startContent={<Icon icon="mdi:translate" width={18} height={18} className="text-default-500" />}
                                listboxProps={{
                                    topContent: renderSelectSearchInput(
                                        languageSearch,
                                        setLanguageSearch,
                                        t('Search languages'),
                                        'region-language-search'
                                    ),
                                }}
                                popoverProps={{ placement: 'bottom' }}
                                classNames={regionSelectClassNames}
                            >
                                {filteredLanguages.map((language) => (
                                    <SelectItem key={language.code} textValue={language.name}>
                                        {language.name}
                                    </SelectItem>
                                ))}
                            </Select>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="bordered" onPress={handleCloseModal} isDisabled={isUpdating}>
                                {t('Cancel')}
                            </Button>
                            <Button
                                onPress={onPressSaveRegion}
                                color="primary"
                                isDisabled={!selectedCountryCode || !selectedLanguageCodes.length || isUpdating}
                                isLoading={isUpdating}
                            >
                                {t('Save')}
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>
        </>
    );
}

export { SettingsIntegrationCard };
export type { AdvisorRegionSetting, AdvisorRegionLanguage };